<template>
  <div>
    <b-sidebar
      id="sidebar-edit-snapshot"
      sidebar-class="sidebar-lg"
      :visible="editSnapshotSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="(val) => mutateEditSnapshotSidebar({ visible: val })"
    >
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Solicitar alteração de snapshot do contrato {{ editSnapshotSidebar.id }}</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>
        <b-row class="justify-content-md-center">
          <b-spinner v-if="loading" big />
        </b-row>
        <template v-if="!loading">
          <!-- Form -->
          <b-form
            @submit.prevent="onSubmit"
            @reset.prevent="resetForm"
            class="p-1"
          >
            <!--
              A tela não grava mais o snapshot. Ela abre uma SOLICITAÇÃO: o snapshot só muda quando
              o Financeiro aprova, e a aprovação grava na mesma transação.
            -->
            <b-alert variant="primary" show class="mb-1">
              <div class="alert-body">
                <small>
                  A alteração do snapshot passa por aprovação do Financeiro. Nada é alterado agora:
                  esta tela registra o pedido.
                </small>
              </div>
            </b-alert>

            <b-row>
              <b-col cols="12">
                <label>Informe o PN a receber a comissão e o respectivo valor percentual (0 a 100%)</label>
              </b-col>
            </b-row>

            <!--
              ⚠️ Desta chamada vale SÓ `last_paid_installment` — é propriedade do CONTRATO. As
              `lines` e os `warnings` dela falam da ÚLTIMA alteração salva, não da que está sendo
              digitada agora: exibi-los aqui mostraria a conta errada.
            -->
            <b-alert
              v-if="billedInstallments > 0"
              variant="warning"
              show
              class="mt-1 mb-0"
              data-testid="snapshot-billed-installments"
            >
              <div class="alert-body">
                <small>
                  <strong>{{ billedInstallments }}</strong> parcela(s) deste contrato já foram
                  faturadas. O recálculo do contrato não alcança as comissões delas — após a
                  aprovação, a tela de solicitações oferece o ajuste retroativo.
                </small>
              </div>
            </b-alert>
            <div
              class="row mt-1"
              v-for="(snapshot, index) in snapshots"
              :key="index"
              style="align-items: flex-end"
            >
              <b-col sm="9">
                <span style="font-size: 10px">{{ snapshot.name }}</span>
                <v-select
                  :id="`snapshot-sidebar-consultant-${index}`"
                  v-model="snapshot.consultant"
                  :options="consultants"
                  @input="inputSelect"
                  :disabled="snapshot.id_consultant_role <= empresaRoleId"
                  label="name"
                >
                  <template #no-options="{ search, searching, loading }">
                    Nenhum PN encontrado neste nível
                  </template>
                </v-select>
                <small
                  v-if="snapshot.id_consultant_role == futuroIndicaRoleId && snapshot.consultant && snapshot.consultant.role"
                  class="text-muted"
                >
                  Nível: {{ snapshot.consultant.role.name }}
                </small>
              </b-col>
              <b-col sm="3" style="display: flex">
                <b-form-input
                  :id="`snapshot-sidebar-percentage-${index}`"
                  v-model="snapshot.percentage_value"
                  min="0"
                  max="100"
                  type="number"
                  step="0.1"
                />
              </b-col>
              <br /><br />
            </div>
            <!--
              A SOMA DOS PERCENTUAIS.

              ⚠️ `validateContractSnapshots` no backend aceita soma **menor ou igual** a 100, não
              exatamente 100. Ou seja: dá para pedir uma distribuição que deixa dinheiro sobrando
              na Futuro. Mostrar o total aqui e na fila de aprovação é o que torna essa sobra
              visível para quem decide.

              Acima de 100 o servidor recusa, então o botão fica bloqueado: poupa o round-trip e
              diz o motivo na hora.
            -->
            <b-alert
              :variant="percentageAlertVariant"
              show
              class="mt-2 mb-0"
              data-testid="snapshot-percentage-total"
            >
              <div class="alert-body d-flex justify-content-between align-items-center">
                <span>Total distribuído</span>
                <strong>{{ percentageTotalLabel }}</strong>
              </div>
              <div v-if="percentageHint" class="alert-body pt-0">
                <small>{{ percentageHint }}</small>
              </div>
            </b-alert>

            <b-form-group
              label="Motivo da alteração"
              label-for="snapshot-request-reason"
              class="mt-2"
            >
              <b-form-textarea
                id="snapshot-request-reason"
                v-model="form.reason"
                rows="3"
                :state="submitAttempted && !reasonIsValid ? false : null"
                placeholder="Descreva por que a divisão de comissão precisa mudar"
              />
              <b-form-invalid-feedback :state="submitAttempted && !reasonIsValid ? false : null">
                Descreva o motivo com pelo menos 20 caracteres.
              </b-form-invalid-feedback>
            </b-form-group>

            <b-form-group label="Impacto esperado" label-for="snapshot-request-impact">
              <b-form-textarea
                id="snapshot-request-impact"
                v-model="form.expected_impact"
                rows="2"
                placeholder="Opcional"
              />
            </b-form-group>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                :disabled="saving || percentageExceedsHundred"
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
              >
                {{ saving ? "Enviando..." : "Solicitar alteração" }}
              </b-button>
            </div>
          </b-form>
        </template>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import _ from "lodash";
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormFile,
  BAvatar,
  BButton,
  BFormInvalidFeedback,
  BFormInput,
  BFormTextarea,
  BRow,
  BCol,
  BSpinner,
  BInputGroup,
  BInputGroupAppend,
  BInputGroupText,
  BAlert,
} from "bootstrap-vue";
import { mapActions, mapMutations, mapGetters } from "vuex";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { apiErrorToast } from "@/helpers/error_messages";
import PercentageInput from "@/modules/shared/components/PercentageInput";
import * as snapshotChangeTypes from "@/modules/snapshot_change/store/types";
import * as types from "../store/types";
import {
  PNH_ROLE_ID,
  PNC_ROLE_ID,
  PNP_ROLE_ID,
  FF_ROLE_ID,
  EMPRESA_ROLE_ID,
  FUTURO_INDICA_ROLE_ID,
  DEPRECATED_ROLE_IDS,
} from "@/constants/consultants";

export default {
  components: {
    // BSV
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormInvalidFeedback,
    BRow,
    BCol,
    BSpinner,
    BInputGroup,
    BInputGroupAppend,
    BInputGroupText,
    BAlert,
    PercentageInput,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      snapshots: [],
      loading: false,
      saving: false,
      submitAttempted: false,
      billedInstallments: 0,
      form: {
        reason: "",
        expected_impact: "",
      },
    };
  },
  computed: {
    ...mapGetters({
      editSnapshotSidebar: types.EDIT_SNAPSHOT_SIDEBAR,
      consultants: types.CONSULTANTS,
    }),
    empresaRoleId: function () {
      return EMPRESA_ROLE_ID;
    },
    futuroIndicaRoleId() {
      return FUTURO_INDICA_ROLE_ID
    },
    /**
     * A soma das linhas COM consultor.
     *
     * As sem consultor são descartadas no submit (`_.filter(this.snapshots, "consultant")`), e
     * contá-las aqui mostraria um total que não é o que vai ser enviado.
     */
    percentageTotal: function () {
      return this.snapshots
        .filter((snapshot) => snapshot.consultant)
        .reduce((total, snapshot) => total + (Number(snapshot.percentage_value) || 0), 0);
    },
    percentageTotalLabel: function () {
      // Duas casas fixas: 33,33 + 33,33 + 33,33 dá 99,99 e o operador precisa ver o centésimo
      // que falta, não um "100" arredondado que esconderia a sobra.
      return `${this.percentageTotal.toFixed(2).replace(".", ",")}%`;
    },
    percentageExceedsHundred: function () {
      return this.percentageTotal > 100.0001;
    },
    percentageIsComplete: function () {
      return Math.abs(this.percentageTotal - 100) <= 0.0001;
    },
    percentageAlertVariant: function () {
      if (this.percentageExceedsHundred) return "danger";
      return this.percentageIsComplete ? "secondary" : "warning";
    },
    percentageHint: function () {
      if (this.percentageExceedsHundred) {
        return "A soma passa de 100%. O servidor recusa o pedido, então o botão fica bloqueado.";
      }

      if (this.percentageIsComplete) return "";

      const sobra = (100 - this.percentageTotal).toFixed(2).replace(".", ",");

      return `Faltam ${sobra}% para fechar 100%. O que não for distribuído fica com a Futuro.`;
    },
    reasonIsValid: function () {
      return (this.form.reason || "").trim().length >= 20;
    },
  },
  setup() {
    return { toast: useToast() };
  },
  methods: {
    ...mapMutations({
      mutateEditSnapshotSidebar: types.MUTATE_EDIT_SNAPSHOT_SIDEBAR,
    }),
    ...mapActions({
      getSnapshots: types.GET_SNAPSHOTS,
      getConsultantsRoles: types.GET_CONSULTANTS_ROLES,
      getRetroactivePreview: types.GET_RETROACTIVE_SPREAD_PREVIEW,
      createRequest: snapshotChangeTypes.CREATE_REQUEST,
    }),
    onShow() {
      this.resetForm();
      this.loadBilledInstallments();

      this.loading = true;
      this.getConsultantsRoles()
        .then((response) => {
          this.snapshots = response.data;
          this.findSnapthost();
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao recuperar os snapshots do contrato. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    },
    findSnapthost() {
      this.getSnapshots(this.editSnapshotSidebar.id)
        .then((response) => {
          this.fillSnapshots(response.data);
          this.snapshots = this.snapshots.filter(
            (s) => !DEPRECATED_ROLE_IDS.includes(s.id_consultant_role) || !!s.consultant
          );
        })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao recuperar os snapshots do contrato. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onSubmit() {
      this.submitAttempted = true;

      if (!this.reasonIsValid) return;

      this.saving = true;

      this.createRequest({
        contractId: this.editSnapshotSidebar.id,
        body: {
          snapshots: this.proposedSnapshots(),
          reason: this.form.reason,
          expected_impact: this.form.expected_impact || undefined,
        },
      })
        .then((response) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Solicitação enviada",
              text: "O snapshot só muda após a aprovação do Financeiro.",
              icon: "CoffeeIcon",
              variant: "success",
            },
          });
          this.$emit("requested", response.data);
          this.finishEditing();
        })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: apiErrorToast(
              error,
              "Ocorreu um erro ao enviar a solicitação. Entre em contato com o setor de TI."
            ),
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    proposedSnapshots() {
      return _.filter(this.snapshots, "consultant");
    },
    /**
     * Aviso de parcelas faturadas. Falhar aqui nao bloqueia o pedido.
     */
    loadBilledInstallments() {
      this.getRetroactivePreview({ contractId: this.editSnapshotSidebar.id })
        .then((response) => {
          this.billedInstallments =
            (response.data && response.data.last_paid_installment) || 0;
        })
        .catch(() => {
          this.billedInstallments = 0;
        });
    },
    resetForm() {
      this.submitAttempted = false;
      this.billedInstallments = 0;
      this.form = {
        reason: "",
        expected_impact: "",
      };
    },
    finishEditing() {
      this.mutateEditSnapshotSidebar({ visible: false });
      this.snapshots = [];
      this.resetForm();
    },
    consultantsByRole(role) {
      //PNH pode ser toda empresa
      if (role == PNH_ROLE_ID) {
        return this.consultants;
      } else if (role == PNC_ROLE_ID || role == PNP_ROLE_ID) {
        //PNCS podem ser toda empresa do role PNC ao PNVP
        return _.filter(this.consultants, (element) => {
          return (
            element.id_consultant_role != PNH_ROLE_ID &&
            element.id_consultant_role != FF_ROLE_ID &&
            element.id_consultant_role != EMPRESA_ROLE_ID
          );
        });
      } else {
        // Retornar os consultores de acordo com a role
        return _.filter(this.consultants, (element) => {
          return element.id_consultant_role == role;
        });
      }
    },
    fillSnapshots(data) {
      _.forEach(this.snapshots, (value) => {
        var snap = _.find(data, {
          id_consultant_role: value.id_consultant_role,
        });
        if(value.id_consultant_role == EMPRESA_ROLE_ID && !snap){
          snap = {
            consultant: {
              name: "FuturoCorp",
              id_consultant_role: EMPRESA_ROLE_ID,
              id: 1,
              receives_commission: true
            },
            depth: 1,
            id_consultant: 1,
            name: "Empresa",
            percentage_value: "0",
          };
        }
        if (!snap) {
          snap = {
            consultant: undefined,
            id: 0,
            id_consultant: 0,
            percentage_value: "0",
          };
        }
        value = _.merge(value, snap);
      });
    },
    inputSelect() {
      this.loading = true;
      _.forEach(this.snapshots, (value) => {
        if (!value.consultant) {
          value.id = 0;
          value.id_consultant = 0;
          value.percentage_value = "0";
        }
      });
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.vs__selected-options {
  flex-wrap: nowrap;
  max-width: calc(
    100% - 50px
  ); /* change this to `- 40px` if you're supporting a `clearable` field; I was not */
}

.vs__selected {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.vs__search {
  position: absolute;
}

.vs--open .vs__search {
  position: static;
}

.vs__dropdown-option {
  white-space: normal;
}
</style>
